import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCost } from "@/utils/costEstimation";
import { Wallet, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PaddleBudgetPurchase } from "./PaddleBudgetPurchase";

interface BudgetManagerProps {
  onBudgetUpdate?: (newBudget: number) => void;
}

export function BudgetManager({ onBudgetUpdate }: BudgetManagerProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [budget, setBudget] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBudget();
    }
  }, [user]);

  const fetchBudget = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("budget")
        .eq("id", user?.id)
        .single();

      if (error) throw error;

      const currentBudget = parseFloat(String(data?.budget || 0));
      setBudget(currentBudget);
    } catch (error) {
      console.error("Error fetching budget:", error);
      toast.error(t("toast.loadBudgetFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            {t("budget.title")}
          </CardTitle>
          <CardDescription>{t("budget.desc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-6 rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20">
            <div className="text-sm text-muted-foreground mb-1">{t("budget.currentBalance")}</div>
            <div className="text-4xl font-bold text-primary">{formatCost(budget)}</div>
            {budget < 0.5 && (
              <Alert className="mt-4" variant="destructive">
                <TrendingDown className="h-4 w-4" />
                <AlertDescription>{t("budget.lowBalance")}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      <PaddleBudgetPurchase
        currentBudget={budget}
        onBudgetUpdate={(newBudget) => {
          setBudget(newBudget);
          onBudgetUpdate?.(newBudget);
        }}
      />
    </div>
  );
}
